import React from 'react';
import { MILITARY, MILITARY_HISTORY, MILITARY_DISCIPLINARY } from 'config/formSections/military';
import { i18n } from 'config/locales';

import { Summary, DateSummary } from 'components/Summary';
import MilitaryService from 'components/Section/Military/History/MilitaryService';
import MilitaryService2013 from 'components/Section/Military/History/MilitaryService2013';
import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import * as formTypes from 'constants/formTypes';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import { isFormSF852013, isPVQForm } from 'helpers/form';
import Comments from 'components/Form/Comments';

const sectionConfig = {
  key: MILITARY_HISTORY.key,
  section: MILITARY.name,
  store: MILITARY.store,
  subsection: MILITARY_HISTORY.name,
  storeKey: MILITARY_HISTORY.storeKey,
};

export const serviceNameDisplay = (service) => {
  let display = (service || {}).value;

  switch (display) {
    case 'Army':
      display = 'Army';
      break;
    case 'AirForce':
      display = 'Air Force';
      break;
    case 'AirNationalGuard':
      display = 'Air National Guard';
      break;
    case 'ArmyNationalGuard':
      display = 'Army National Guard';
      break;
    case 'CoastGuard':
      display = 'Coast Guard';
      break;
    case 'MarineCorps':
      display = 'Marine Corps';
      break;
    case 'Navy':
      display = 'Navy';
      break;
    case 'MerchantMarine':
      display = 'Merchant Marine';
      break;
    case 'NationalOceanicAtmosphericAdministration':
      display = 'National Oceanic and Atmospheric Administration';
      break;
    case 'PublicHealthService':
      display = 'Public Health Service';
      break;
    case 'SpaceForce':
      display = 'Space Force';
      break;
    default:
      display = 'Unknown';
  }

  return display;
};

export class History extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.updateList = this.updateList.bind(this);
    this.updateServed = this.updateServed.bind(this);
    this.updateServedMerchantMarine = this.updateServedMerchantMarine.bind(this);
    this.updateComments = this.updateComments.bind(this);
    this.updateSummaryComments = this.updateSummaryComments.bind(this);
    this.summary = this.summary.bind(this);
  }

  update = (queue) => {
    let updateSections = [];
    if (queue.HasServed?.value !== this.props.HasServed?.value) {
      /* If hasServed changed we need to tell other subsections that are reliant to update validation */
      if (!isPVQForm(this.props.formType)) {
        updateSections = [MILITARY_DISCIPLINARY.key];
      }
    }

    this.props.onUpdate(
      this.storeKey,
      {
        HasServed: this.props.HasServed,
        HasServedMerchantMarine: this.props.HasServedMerchantMarine,
        List: this.props.List,
        Comments: this.props.Comments,
        SummaryComments: this.props.SummaryComments,
        ...queue,
      },
      updateSections
    );
  };

  updateServed = (values) => {
    // If there is no history clear out any previously entered data
    this.update({
      HasServed: values,
      HasServedMerchantMarine: this.props.HasServedMerchantMarine,
      List:
        values.value === 'Yes' || this.props.HasServedMerchantMarine.value === 'Yes'
          ? this.props.List
          : { items: [], branch: {} },
    });
  };

  updateServedMerchantMarine = (values) => {
    // If there is no history clear out any previously entered data
    this.update({
      HasServed: this.props.HasServed,
      HasServedMerchantMarine: values,
      List:
        values.value === 'Yes' || this.props.HasServed.value === 'Yes'
          ? this.props.List
          : { items: [], branch: {} },
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  updateComments(value) {
    this.update({
      Comments: value,
    });
  }

  updateSummaryComments(value) {
    this.update({
      SummaryComments: value,
    });
  }

  /**
   * Assists in rendering the summary section.
   */
  summary = (item, index) => {
    const o = (item || {}).Item || {};
    const dates = DateSummary(o.Dates);
    const service = serviceNameDisplay(o.Service);

    return Summary({
      type: i18n.t('military.history.collection.summary.item'),
      index,
      left: service,
      right: dates,
      placeholder: i18n.t('military.history.collection.summary.unknown'),
    });
  };

  render() {
    const { errors, formType, formVersion } = this.props;
    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);

    let servedLabel = '';

    if (this.props.formType === formTypes.SF852013) {
      servedLabel = i18n.t('military.history.heading.served2013');
    } else {
      servedLabel = i18n.t('military.history.heading.served');
    }

    const isPvq = isPVQForm(this.props.formType);

    return isPvq ? (
      <div
        className="section-content military-history"
        data-section={MILITARY.key}
        data-subsection={MILITARY_HISTORY.key}
      >
        {/* PVQ: U.S. Military and U.S. Uniformed Service */}
        <h1 className="section-header">{i18n.t('military.pvq.destination.history')}</h1>
        {/* PVQ: Have you ever served in the U.S. military or a U.S. uniformed service? */}
        {/* PVQ: Do you have another period of service to report? */}
        <Branch
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...this.props.HasServed}
          attribute="HasServed"
          className="served"
          exactAttribute
          help="military.pvq.history.help.served"
          label={i18n.t('military.pvq.history.heading.served')}
          labelSize="h4"
          name="has_served"
          onError={this.handleError}
          onUpdate={this.updateServed}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          warning
        />
        {this.props.HasServed.value === 'Yes' && (
          <Accordion
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...this.props.List}
            appendLabel={i18n.t('military.pvq.history.collection.append')}
            appendTitle={i18n.t('military.pvq.history.collection.appendTitle')}
            defaultState={this.props.defaultState}
            description={i18n.t('military.pvq.history.collection.summary.title')}
            errors={accordionErrors}
            onError={this.handleError}
            onUpdate={this.updateList}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary}
          >
            <MilitaryService
              bind
              formType={this.props.formType}
              name="Item"
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Accordion>
        )}
      </div>
    ) : (
      <div
        className="section-content military-history"
        data-section={MILITARY.key}
        data-subsection={MILITARY_HISTORY.key}
      >
        <h1 className="section-header">{i18n.t('military.destination.history')}</h1>
        <Branch
          name="has_served"
          label={servedLabel}
          labelSize="h4"
          className="served"
          {...this.props.HasServed}
          help="military.history.help.served"
          warning
          onUpdate={this.updateServed} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          scrollIntoView={this.props.scrollIntoView}
          attribute="HasServed"
          exactAttribute
        />
        <Show when={this.props.formType === formTypes.SF852013}>
          <Branch
            name="has_served_merchant_marine"
            label={i18n.t('military.history.heading.servedmerchantmarine')}
            labelSize="h4"
            className="servedmerchantmarine"
            {...this.props.HasServedMerchantMarine}
            help="military.history.help.servedmerchantmarine"
            warning
            onUpdate={this.updateServedMerchantMarine} // eslint-disable-line @typescript-eslint/unbound-method
            required={this.props.required}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            scrollIntoView={this.props.scrollIntoView}
            attribute="HasServedMerchantMarine"
          />
        </Show>
        {!isFormSF852013(formType, formVersion) && (
          <Comments
            title={i18n.t('military.history.comments')}
            name="Comments"
            attribute="Comments"
            className="optional-comments"
            {...this.props.Comments}
            onUpdate={this.updateComments}
            onError={this.props.onError}
            required={false}
          />
        )}
        <Show
          when={
            this.props.HasServed.value === 'Yes'
            || this.props.HasServedMerchantMarine.value === 'Yes'
          }
        >
          <Accordion
            {...this.props.List}
            defaultState={this.props.defaultState}
            scrollToBottom={this.props.scrollToBottom}
            onUpdate={this.updateList} // eslint-disable-line @typescript-eslint/unbound-method
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            summary={this.summary} // eslint-disable-line @typescript-eslint/unbound-method
            description={i18n.t('military.history.collection.summary.title')}
            appendTitle={i18n.t('military.history.collection.appendTitle')}
            appendLabel={i18n.t('military.history.collection.append')}
            errors={accordionErrors}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Show when={this.props.formType === formTypes.SF852013}>
              <MilitaryService2013
                name="Item"
                bind
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                formType={this.props.formType}
              />
            </Show>
            <Show when={this.props.formType !== formTypes.SF852013}>
              <MilitaryService
                name="Item"
                bind
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
              />
            </Show>
          </Accordion>
          {!isFormSF852013(formType, formVersion) && (
            <Comments
              title={i18n.t('military.history.summarycomments')}
              name="SummaryComments"
              attribute="SummaryComments"
              className="summary-comments"
              {...this.props.SummaryComments}
              onUpdate={this.updateSummaryComments}
              onError={this.props.onError}
              required={false}
            />
          )}
        </Show>
      </div>
    );
  }
}

History.defaultProps = {
  List: { items: [] },
  HasServed: {},
  HasServedMerchantMarine: {},
  Comments: {},
  SummaryComments: {},
  onUpdate: () => { },
  onError: (value, arr) => arr,
  section: 'military',
  subsection: 'history',
  addressBooks: {},
  dispatch: () => { },
  defaultState: true,
  required: false,
  errors: [],
};

export default connectSubsection(History, sectionConfig);
